import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import {
  HR_LEAVE_STATUSES,
  HrCalendar,
  HrEmployee,
  HrHoliday,
  HrLeaveBalance,
  HrLeaveCalendarEntry,
  HrLeaveDashboard,
  HrLeaveRequest,
  HrLeaveType
} from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-leave',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    PageFormSectionComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-leave.component.html',
  styleUrls: ['./hr-leave.component.scss']
})
export class HrLeaveComponent implements OnInit {
  activeListTab = 'requests';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  dashboard: HrLeaveDashboard = {};
  requests: HrLeaveRequest[] = [];
  calendarEntries: HrLeaveCalendarEntry[] = [];
  balances: HrLeaveBalance[] = [];
  calendars: HrCalendar[] = [];
  holidays: HrHoliday[] = [];
  employees: HrEmployee[] = [];

  requestForm: HrLeaveRequest = this.emptyRequest();
  typeForm: HrLeaveType = this.emptyType();
  calendarForm: HrCalendar = this.emptyCalendar();
  holidayForm: HrHoliday = this.emptyHoliday();
  filters: { status?: string; employeeId?: number; from?: string; to?: string } = {};
  activeDialog?: 'request' | 'type' | 'calendar' | 'holiday';

  readonly canRequestLeave = this.permissionService.hasPermission(app_permissions.hr.leave.request);
  readonly canApproveLeave = this.permissionService.hasPermission(app_permissions.hr.leave.approve);
  readonly canManageLeave = this.permissionService.hasPermission(app_permissions.hr.leave.manage);
  readonly booleanOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];
  readonly statusOptions = HR_LEAVE_STATUSES.map(value => ({ label: this.format(value), value }));

  ngOnInit(): void {
    this.loadEmployees();
    this.load();
  }

  get pendingApprovals(): HrLeaveRequest[] {
    return this.dashboard.pendingApprovals || [];
  }

  get leaveTypes(): HrLeaveType[] {
    return this.dashboard.leaveTypes || [];
  }

  get leaveTypeOptions(): { label: string; value: number | undefined }[] {
    return this.leaveTypes.map(type => ({
      label: `${type.leaveName || type.leaveCode}${type.systemDefault ? ' · Default' : ''}`,
      value: type.leaveTypeId
    }));
  }

  get employeeOptions(): { label: string; value: number | undefined }[] {
    return this.employees.map(employee => ({
      label: this.employeeLabel(employee),
      value: employee.employeeId
    }));
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Pending Requests', value: this.dashboard.pendingRequests ?? 0, tone: (this.dashboard.pendingRequests ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Approved This Month', value: this.dashboard.approvedThisMonth ?? 0, tone: 'success' },
      { label: 'Rejected This Month', value: this.dashboard.rejectedThisMonth ?? 0, tone: (this.dashboard.rejectedThisMonth ?? 0) > 0 ? 'danger' : 'success' },
      { label: 'On Leave Today', value: this.dashboard.employeesOnLeaveToday ?? 0, tone: 'info' },
      { label: 'Conflict Warnings', value: this.dashboard.requestsWithConflicts ?? 0, tone: (this.dashboard.requestsWithConflicts ?? 0) > 0 ? 'warn' : 'success' },
      { label: 'Active Leave Types', value: this.dashboard.activeLeaveTypes ?? 0, tone: 'info' }
    ];
  }

  load(): void {
    this.loading = true;
    this.hrService.leaveDashboard().subscribe({
      next: response => {
        this.activeDialog = undefined;
        this.dashboard = response.data || {};
        this.balances = this.dashboard.balances || [];
        this.loadRequests();
        this.loadCalendar();
        this.loadSettings();
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to load leave dashboard.' });
      }
    });
  }

  loadRequests(): void {
    this.hrService.leaveRequests(undefined, this.filters.status, this.filters.from, this.filters.to, this.toNumber(this.filters.employeeId), 250).subscribe({
      next: response => this.requests = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to load leave requests.' })
    });
  }

  loadCalendar(): void {
    const today = new Date();
    const from = this.isoDate(new Date(today.getFullYear(), today.getMonth(), 1));
    const to = this.isoDate(new Date(today.getFullYear(), today.getMonth() + 2, 0));
    this.hrService.leaveCalendar(undefined, from, to).subscribe({
      next: response => this.calendarEntries = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to load leave calendar.' })
    });
  }

  loadSettings(): void {
    this.hrService.leaveCalendars().subscribe({
      next: response => this.calendars = response.data || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to load leave calendars.' })
    });
    this.hrService.leaveHolidays().subscribe({
      next: response => {
        this.holidays = response.data || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to load holidays.' });
      }
    });
  }

  loadEmployees(): void {
    this.hrService.employees({ owningCompanyId: 250, pageSize: 500 }).subscribe({
      next: response => this.employees = response.items || [],
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to load employee dropdown.' })
    });
  }

  submitLeave(): void {
    const employeeId = this.toNumber(this.requestForm.employeeId);
    if (!employeeId) return;
    this.hrService.submitLeaveRequest(employeeId, { ...this.requestForm, employeeId }).subscribe({
      next: response => {
        this.activeDialog = undefined;
        const warning = response.data?.assignmentConflictWarning ? ' Assignment conflicts were detected.' : '';
        this.messageService.add({ severity: 'success', summary: 'HR Leave', detail: `Leave request submitted.${warning}` });
        this.resetRequest();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to submit leave request.' })
    });
  }

  approve(request: HrLeaveRequest): void {
    if (!request.leaveRequestId) return;
    this.hrService.approveLeaveRequest(request.leaveRequestId, { decisionReason: 'Approved from HR leave workspace.' }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Leave', detail: 'Leave request approved.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to approve leave request.' })
    });
  }

  reject(request: HrLeaveRequest): void {
    if (!request.leaveRequestId) return;
    this.hrService.rejectLeaveRequest(request.leaveRequestId, { decisionReason: 'Rejected from HR leave workspace.' }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'HR Leave', detail: 'Leave request rejected.' });
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to reject leave request.' })
    });
  }

  recalculateBalances(): void {
    const today = new Date();
    this.hrService.recalculateLeaveBalances({
      periodStart: `${today.getFullYear()}-01-01`,
      periodEnd: `${today.getFullYear()}-12-31`
    }).subscribe({
      next: response => {
        this.balances = response.data || [];
        this.messageService.add({ severity: 'success', summary: 'HR Leave', detail: 'Leave balances recalculated.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to recalculate balances.' })
    });
  }

  saveType(): void {
    this.hrService.saveLeaveType(this.typeForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.messageService.add({ severity: 'success', summary: 'HR Leave', detail: 'Leave type saved.' });
        this.resetType();
        this.load();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to save leave type.' })
    });
  }

  editType(type: HrLeaveType): void {
    this.typeForm = { ...type };
    this.activeDialog = 'type';
  }

  saveCalendar(): void {
    this.hrService.saveLeaveCalendar(this.calendarForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.messageService.add({ severity: 'success', summary: 'HR Leave', detail: 'Calendar saved.' });
        this.resetCalendar();
        this.loadSettings();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to save calendar.' })
    });
  }

  editCalendar(calendar: HrCalendar): void {
    this.calendarForm = { ...calendar };
    this.activeDialog = 'calendar';
  }

  saveHoliday(): void {
    this.hrService.saveLeaveHoliday(this.holidayForm).subscribe({
      next: () => {
        this.activeDialog = undefined;
        this.messageService.add({ severity: 'success', summary: 'HR Leave', detail: 'Holiday saved.' });
        this.resetHoliday();
        this.loadSettings();
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR Leave', detail: 'Unable to save holiday.' })
    });
  }

  editHoliday(holiday: HrHoliday): void {
    this.holidayForm = { ...holiday };
    this.activeDialog = 'holiday';
  }

  clearFilters(): void {
    this.filters = {};
    this.loadRequests();
  }

  resetRequest(): void {
    this.requestForm = this.emptyRequest();
  }

  resetType(): void {
    this.typeForm = this.emptyType();
  }

  resetCalendar(): void {
    this.calendarForm = this.emptyCalendar();
  }

  resetHoliday(): void {
    this.holidayForm = this.emptyHoliday();
  }

  openDialog(dialog: 'request' | 'type' | 'calendar' | 'holiday'): void {
    if (dialog === 'request') this.resetRequest();
    if (dialog === 'type') this.resetType();
    if (dialog === 'calendar') this.resetCalendar();
    if (dialog === 'holiday') this.resetHoliday();
    this.activeDialog = dialog;
  }

  dialogVisible(dialog: 'request' | 'type' | 'calendar' | 'holiday'): boolean {
    return this.activeDialog === dialog;
  }

  setDialogVisible(dialog: 'request' | 'type' | 'calendar' | 'holiday', visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (status === 'APPROVED') return 'success';
    if (status === 'PENDING') return 'warn';
    if (status === 'REJECTED') return 'danger';
    if (status === 'CANCELLED') return 'secondary';
    return 'info';
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private employeeLabel(employee: HrEmployee): string {
    const name = employee.displayName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || 'Employee';
    const number = employee.employeeNumber || employee.employeeId || '-';
    const department = employee.departmentName ? ` - ${employee.departmentName}` : '';
    return `${name} (${number})${department}`;
  }

  private emptyRequest(): HrLeaveRequest {
    return { emergencyLeave: false };
  }

  private emptyType(): HrLeaveType {
    return {
      paidLeave: true,
      requiresBalance: true,
      annualEntitlementDays: 0,
      allowNegativeBalance: false,
      emergencyLeave: false,
      active: true,
      colorCode: '#0ea5e9'
    };
  }

  private emptyCalendar(): HrCalendar {
    return {
      weekendDays: 'FRIDAY,SATURDAY',
      defaultCalendar: false,
      active: true
    };
  }

  private emptyHoliday(): HrHoliday {
    return {
      paidHoliday: true,
      active: true
    };
  }

  private toNumber(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  }

  private isoDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }
}
